import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateCustomerInput, CustomerDetail, CustomerListItem, UpdateCustomerInput } from '@wrapforce/shared';
import { workspacePath } from '@wrapforce/shared/api-client';
import { api } from '@/lib/api';

export function useCustomers(workspaceId: string | null, params: { query?: string }) {
  return useQuery({
    queryKey: [workspaceId, 'customers', params],
    queryFn: ({ signal }) => api.get<{ items: CustomerListItem[]; nextCursor: string | null }>(workspacePath(workspaceId!, '/customers'), { query: params, signal }),
    enabled: !!workspaceId,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

export function useCustomer(workspaceId: string | null, customerId: string | undefined) {
  return useQuery({
    queryKey: [workspaceId, 'customers', customerId],
    queryFn: () => api.get<CustomerDetail>(workspacePath(workspaceId!, `/customers/${customerId}`)),
    enabled: !!workspaceId && !!customerId,
    staleTime: 10_000,
  });
}

export function useCreateCustomer(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCustomerInput) => api.post<{ id: string }>(workspacePath(workspaceId!, '/customers'), input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [workspaceId, 'customers'] }),
  });
}

export function useUpdateCustomer(workspaceId: string | null, customerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateCustomerInput) => api.patch(workspacePath(workspaceId!, `/customers/${customerId}`), input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'customers'] });
    },
  });
}
